import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useModal } from "../../context/ConfirmModalContext";
import type User from "../../models/User";
import RBACModal from "../../Modal/staff/RBACModal";
import {
  FaShareAlt, FaPrint,
  FaChevronDown, FaChevronRight
} from "react-icons/fa";

import {
  MdEdit,
  MdLockPerson,
  MdCalendarToday,
  MdOutlinePayments,
  MdDelete,
  MdPowerSettingsNew,
  MdMoreVert,
  MdMail,
  MdPhone,
  MdArrowDropDown,
  MdPersonAdd,
} from "react-icons/md";
import {
  requestTableColumn, synchroniseStaffs,
  fetchAndSetRequests, filterStaffsByRole,
  getUser, initPayroll, payRollColumns,
} from "../../utils/user-helper";
import { initAllStores } from "../../utils/cache";
import { sendRequest, useHandleApiConflict } from "../../utils/api";
import FullPageSpinner from "../../components/FullPageSpinner";
import { useToast } from "../../context/ToastContext";
import InviteModal from "../../Modal/staff/InviteModal";
import { FaTimes } from "react-icons/fa";
import { MenuOption, Table, TableMenu } from "../../components/Table";
import { capitalizeFirst } from "../../utils/string";
import Loan from "../../models/Loan";
import { formatDate } from "../../utils/date";
import Leave from "../../models/Leave";
import { COMPANY_NAME, PAGINATION_PAGE_NUMBER } from "../../constants";
import RequestDetailsModal from "../../Modal/staff/RequestDetailsModal";
import { checkAccess, computeStaffRequestActionAccess } from "../../utils/access";
import { DashboardHeader } from "../../components/DashboardHeader";
import PayrollModal, { printPayroll } from "../../Modal/accounting/PayrollModal";
import Payroll from "../../models/Payroll";
import Payslip from "../../Modal/accounting/PaySlip";
import Approval from "../../models/Approver";
import Site from "../../models/Site";
import { Day } from "../../types/day";
import ProfileAvatar from "../../components/ProfileAvatar";


{/* ---------- Collapsible Section Component ---------- */ }
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(true);

  return (
    <div className="bg-gray-50 rounded-lg shadow mb-6">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex justify-between items-center px-4 py-3 border-b text-left font-medium hover:bg-gray-100 transition"
      >
        <span>{title}</span>
        <span className="text-gray-600">
          {open ? FaChevronDown({}) : FaChevronRight({})}
        </span>
      </button>

      <div
        className={`transition-all duration-300 overflow-hidden ${open ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0"}`}
      >
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

export default function UserPage() {
  const navigate = useNavigate();
  const { openModal, closeModal } = useModal();
  const { showToast } = useToast();

  const { handleApiConflict } = useHandleApiConflict({
    onReload: () => {
      if (showRequestsList) fetchRequests(showRequestsList)
    }
  });

  const [sites, setSites] = useState<Site[]>([]);

  const [activeTab, setActiveTab] = useState<"current" | "former" | "requests" | "payroll">(
    "current"
  );
  const [showInviteModal, setShowInviteModal] = useState(false);
  // const [inviteEmail, setInviteEmail] = useState("");
  const [showAddDropdown, setShowAddDropdown] = useState(false);
  const [showRequestDropdown, setShowRequestDropdown] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  // const [searchQuery, setSearchQuery] = useState("");

  const [showRBACModal, setShowRBACModal] = useState(false);
  const [showPayrollModal, setShowPayrollModal] = useState(false);
  const [showPayslip, setShowPayslip] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<User | null>(null);

  const [requestModal, setRequestModal] = (
    useState<{ type: RequestType, data: Loan | Leave, show: boolean }>({} as any)
  );

  const [search, setSearch] = useState("");

  const [loading, setLoading] = useState<boolean>(false);
  const [staffs, setStaffs] = useState<User[]>([]);

  const wrapperRef = useRef<HTMLDivElement>(null);

  const [earnings, setEarnings] = useState<PayRoll[]>([] as PayRoll[]);
  const [deductions, setDeductions] = useState<PayRoll[]>([] as PayRoll[]);

  const [user, setUser] = useState<User>({} as User);

  // Roster Modal
  const [showRosterModal, setShowRosterModal] = useState(false);
  const [rosterSchedule, setRosterSchedule] = useState({
    monday: { shift: "day", branch: "" },
    tuesday: { shift: "day", branch: "" },
    wednesday: { shift: "day", branch: "" },
    thursday: { shift: "day", branch: "" },
    friday: { shift: "day", branch: "" },
    saturday: { shift: "day", branch: "" },
    sunday: { shift: "day", branch: "" },
  });

  const [showRequestsList, setShowRequestsList] = useState<RequestType | null>(null);
  const [loans, setLoans] = useState<Loan[]>([]);
  const [leaves, setLeaves] = useState<Leave[]>([]);
  const [payrolls, setPayrolls] = useState<Payroll[]>([]);

  const [month, setMonth] = useState("");
  // const [end, setEnd] = useState("");
  const [netpay, setNetpay] = useState(0);


  const [showFilter] = useState(false);

  const [access, setAccess] = useState({
    leaveActionAccess: false, loanActionAccess: false
  });

  // Close dropdowns/menus on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowAddDropdown(false);
        setShowRequestDropdown(false);
        setOpenMenuId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const loanAccess = computeStaffRequestActionAccess("loan");
    const leaveAccess = computeStaffRequestActionAccess("leave");
    setAccess({ ...loanAccess, ...leaveAccess });
  }, [])

  useEffect(() => {
    const getCurrentUser = async () => {
      const data = await getUser();
      if (data) {
        const { user } = data;
        setUser(user);
      }
    };
    getCurrentUser();
  }, [])

  const fetchStaffs = async () => {
    setLoading(true);
    try {
      const staffs = await synchroniseStaffs();
      setStaffs(staffs);
    } catch (err: any) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStaffs();
  }, []);

  const addUserClick = () => {
    navigate("/staffs/register")
  };

  const getStaffs = async (isDelete: boolean, isCuurentStaff: boolean) => {
    const cache = await initAllStores();
    const staffs: User[] = await cache.getAllBy("staffs", { isDelete }, "firstName");
    const filteredStaffs = await filterStaffsByRole(staffs, isCuurentStaff);
    setActiveTab(isDelete ? "former" : "current");
    setStaffs(filteredStaffs);
    setShowRequestsList(null);
  };

  const confirmDeactivateModal = (staff: User) => {
    const isDeactivated = staff.isDelete;

    const title = `Confirm Account ${isDeactivated ? "Activation" : "Deactivation"}`;
    // const actionText = isDeactivated ? "activate" : "deactivate";
    const confirmButton = `Yes, ${isDeactivated ? "Activate" : "Deactivate"}`;

    const body = isDeactivated
      ? `Are you sure you want to activate ${staff.firstName} ${staff.lastName}? Their account will be restored and re-enabled for access.`
      : `Are you sure you want to deactivate ${staff.firstName} ${staff.lastName}? The staff record will be archived and can be restored later if needed.`;

    return openModal(
      title, body,
      () => deactivateUser(staff),
      true, confirmButton, "Cancel", "red"
    );
  };

  async function deactivateUser(staff: User) {
    setLoading(true);
    try {
      const cache = await initAllStores();
      const userId = staff.id;
      const action = staff.isDelete ? "activate" : "deactivate"
      await Promise.all([
        sendRequest("PUT", `/users/${userId}/${action}`),
        await cache.updateManyBy("staffs", [userId], { isDelete: !staff.isDelete })
      ]);

      showToast(
        `Staff ${staff.isDelete ? "Activatation" : "Deactivation"} Successfully.`,
        { type: "success" }
      );

      setStaffs((prev) =>
        prev.filter((staff) => staff.id !== userId)
      );
    } catch (err: any) {
      showToast(
        "Failed to deactivate staff. "
        + "Please check your network and try again.",
        { type: "error" }
      );
      if (err.status === 404) synchroniseStaffs();
    } finally {
      setLoading(false);
    }
  }


  const deleteUser = (staff: User) => {
    return openModal(
      "Confirm Account Removal",
      `Are you sure you want to remove ${staff.firstName} ${staff.lastName}'s account? `
      // + "This action is irreversible and all associated records will be lost.",
      + "This action is irreversible.",
      async () => {
        setLoading(true);
        try {
          const cache = await initAllStores();
          const userId = staff.id;
          await Promise.all([
            sendRequest("DELETE", `/users/${userId}/delete`),
            cache.deleteOne("staffs", userId)
          ]);

          showToast(`Staff ${staff.firstName} ${staff.lastName} removed successfully.`, {
            type: "success",
          });

          setStaffs((prev) =>
            prev.filter((staff) => staff.id !== userId)
          );
        } catch (err: any) {
          showToast(
            `Failed to remove ${staff.firstName} ${staff.lastName}. `
            + "Please try again", { type: "error" }
          );
          if (err.status === 404) synchroniseStaffs();
          console.log(err)
        } finally {
          setLoading(false);
        }
      },
      true, "Yes, Remove", "Cancel", "red"
    );
  };


  async function upserPermission(staffId: string, permissions: any) {
    setLoading(true);
    try {
      const cache = await initAllStores();
      await Promise.all([
        sendRequest("POST", `/users/${staffId}/permissions`, { permissions }),
        cache.updateManyBy("staffs", [staffId], { permissions })
      ]);

      setStaffs(prev => prev.map((p: User) => ({ ...p, permissions })));
      closeModal();
      showToast("Permissions updated successfully.", { type: "success" });
      openModal(
        "Permission Update Notice ⚠️",
        `The updated permission for this staff will take effect after they log out and log back in.
        --------------------------------------------------
        Please inform the staff to re-login for the changes to apply.`,
        closeModal, false, "Close"
      );

    } catch (err: any) {
      showToast(
        "Failed to update permissions. Please check your network and try again.",
        { type: "error" }
      );
      if (err.status === 404) synchroniseStaffs();
    } finally {
      setLoading(false);
    }
  }


  const upsertRoster = async () => {
    setLoading(true);
    try {

      // console.log(rosterSchedule)
      await sendRequest("POST", `/users/${selectedStaff?.id}/rosters`, {
        rosterSchedule,
      });
      showToast("Roster updated successfully.", { type: "success" });
      setShowRosterModal(false);
    } catch (err) {
      showToast("Failed to update roster. Try again.", { type: "error" });
    } finally {
      setLoading(false);
    }
  };


  const fetchRequests = async (requestType: RequestType) => {
    setLoading(true);
    try {
      // const req = await sendRequest("GET", `/requests/${requestType}`);
      if (requestType === "loan")
        await fetchAndSetRequests<Loan>("/requests/loan", setLoans, "loan");
      else
        await fetchAndSetRequests<Leave>("/requests/leave", setLeaves, "leave");

    } catch (err) {
      console.log(err)
      showToast(
        "Unable to fetch loan requests at the moment. "
        + "Please try again later.", { type: "error" }
      );
    } finally {
      setLoading(false);
    }
  }


  const isApprovedByCurrentUser = (approvals: Approval[]) => {
    const isShow = approvals.find(
      app => app.status === "pending" &&
        app?.approveBy?.id === user.id
    );
    return !!isShow;
  }

  const handleStaffRequestAction = async (
    type: RequestType,
    requestId: string,
    action: "approve" | "reject" | "delete",
    requester?: { role?: string, portfolio?: string },
    approvals?: Approval[]
  ) => {

    const hr = "hrm";
    const manager = "managing director";

    const userRole = user?.portfolio?.toLocaleLowerCase();
    const isApproveLeave = (
      [hr, manager, "ceo"].includes(userRole)
    );

    const isAllowDelete = ["ceo"].includes(userRole);

    const isApproveLoan = (
      [hr, manager, "ceo", "accountant"].includes(userRole)
    );


    if (!isApproveLeave && type === "leave") {
      const isAccess = checkAccess(
        "leaveActionAccess",
        { leaeActionAccess: isApproveLeave },
        openModal, closeModal
      );
      if (!isAccess) return;
    } else if (!isApproveLoan && type === "leave") {
      const isAccess = checkAccess(
        "loanActionAccess",
        { loanActionAccess: isApproveLoan },
        openModal, closeModal
      );
      if (!isAccess) return;
    } else if (!isAllowDelete && action === "delete") {
      const isAccess = checkAccess(
        "deleteStaffRequest",
        { loanActionAccess: isApproveLoan },
        openModal, closeModal
      );
      if (!isAccess) return;
    }

    // Check who can approved
    const isApprovedByAdmin = (approvals: Approval[], portfolio: string) => {
      const approvedBy = approvals.find(
        app => app.status !== "pending" &&
          app?.approveBy?.portfolio?.toLocaleLowerCase()
          === portfolio.toLocaleLowerCase()
      );
      return !!approvedBy;
    }


    const pendingApprovalAlert = (portfolio: string) => {
      alert(
        "This request has not been approved" +
        ` by the ${portfolio} or has been rejected.`
      );
    }
    const currentUserPortfolio = user?.portfolio?.toLocaleLowerCase();

    if (
      requester?.role === "admin" &&
      currentUserPortfolio !== "ceo" &&
      !isApprovedByAdmin(approvals ?? [], "ceo")
    ) {
      return pendingApprovalAlert("CEO");
    }

    if (type === "leave") {

      if (
        !isApprovedByAdmin(approvals ?? [], hr)
        && currentUserPortfolio === manager
      ) {
        return pendingApprovalAlert("HRM");
      }
    } else if (type === "loan") {
      if (
        currentUserPortfolio === "accountant" &&
        !isApprovedByAdmin(approvals ?? [], manager)
      ) {
        return pendingApprovalAlert("Manager Director");
      } else if (
        !isApprovedByAdmin(approvals ?? [], "accountant") &&
        currentUserPortfolio === hr
      ) {
        return pendingApprovalAlert("Accountant");
      }
    }

    // const user = await getUserHotel();
    function updateRequestStatus<T extends { id: string; status?: string }>(
      updater: React.Dispatch<React.SetStateAction<T[]>>,
      requestId: string,
      action: "approve" | "reject" | "delete"
    ) {

      if (action === "delete") {
        // Remove from UI list
        updater((prev) => prev.filter((req) => req.id !== requestId));
      } else {
        updater((prev) =>
          prev.map((req) =>
            req.id === requestId
              ? {
                ...req, decisionDate: formatDate(new Date()),
                // status: `${capitalizeFirst(formatStatus(action))}`,
              }
              : req
          )
        );
      }
    }

    function formatStatus(action: string) {
      if (action === "reject") return "rejected";
      if (action === "approve") return "approved";
      if (action === "delete") return "deleted";
      return action;
    }

    const actionLabel = capitalizeFirst(action);
    const typeLabel = capitalizeFirst(type);

    return openModal(
      `${actionLabel} ${typeLabel} Request`,
      `You're about to ${action} this ${type} request. Please confirm before proceeding.`,
      async () => {
        setLoading(true);
        try {
          // Send action to backend


          if (action === "delete") {
            await sendRequest("DELETE", `/requests/${type}/${requestId}/delete`);
          } else {
            await sendRequest("PUT", `/requests/${type}/${requestId}/${action}`);
          }

          // Update UI
          if (type === "loan") updateRequestStatus(setLoans, requestId, action);
          else updateRequestStatus(setLeaves, requestId, action);

          showToast(
            `${typeLabel} Request ${actionLabel}${action === "delete" ? "d" : "d Successfully."
            }`,
            { type: "success" }
          );
        } catch (error: any) {

          const handled = handleApiConflict(error, type);
          if (handled) return;

          const restrictAccess = (): boolean => {
            const isAccess = checkAccess(
              `${type}ActionAccess`, { [`${type}ActionAccess`]: false }, openModal, closeModal
            );
            return isAccess;
          }

          if (error.status === 422 && action === "approve") {
            const rx = JSON.parse(error.response.data.error);

            return openModal(
              "Leave Approval Limit Exceeded ⚠️",
              `This leave request exceeds the employee's available leave balance:
                --------------------------------------------
                Requested Days: ${rx.requestedDays}
                Remaining Days: ${rx.remainingDays}
                Used Days: ${rx.usedDays}
                --------------------------------------------
                Please adjust the leave duration or decline the request.`,
              closeModal,
              false,
              "Okay"
            );
          }

          if (error.status === 403) {
            if (type === "loan") {
              const isAccess = restrictAccess();
              if (!isAccess) return;
            } else {
              const isAccess = restrictAccess();
              if (!isAccess) return;
            }
          } else {
            console.error(error);
            showToast(
              `Failed to ${actionLabel} ${typeLabel} Request. Please try again.`,
              { type: "error" }
            );
          }
        } finally {
          setLoading(false);
        }
      },
      true,
      `Yes, ${actionLabel}`,
      "Cancel"
    );
  };


  const getPayrolls = async (month: string) => {

    setLoading(true);

    try {
      let url = "/payrolls";
      const params = new URLSearchParams({});

      // 👉 append date params only if provided
      if (month) params.set("start", month);

      url += `?${params.toString()}`;
      const response: Payroll[] = await sendRequest("GET", url);

      // 🔥 Calculate total BEFORE formatting
      const totalNet = response.reduce(
        (sum, pl) => sum + Number(pl.netPay),
        0
      );

      setNetpay(totalNet);

      // setTotalNet(totalNet); // store as number
      setPayrolls(
        response.map(pl => ({
          ...pl,
          position: pl.user.portfolio,
          totalDeductions: pl.totalDeductions.toLocaleString(),
          totalEarnings: pl.totalEarnings.toLocaleString(),
          netPay: pl.netPay.toLocaleString(),
          createdAt: formatDate(pl.createdAt),
          name: (
            pl.user.firstName + " " + pl.user.lastName
          ),
          accountNo: pl.user.accountNo
        }))
      );

    } catch (err: any) {
      console.log(err)
      showToast(
        "Failed to load payrolls. Please check your network and try again.",
        { type: "error" }
      );
    } finally {
      setLoading(false);
    }
  }

  const sharePayslip = async () => {
    return openModal(
      "Confirm Bulk Payslip Distribution",
      "You’re about to distribute payslips to multiple staff members at once. "
      + "Do you want to continue?",
      async () => {
        setLoading(true);
        try {

          const staffIds = staffs.filter(st => !st.isDelete).map(st => st.id);
          await sendRequest("POST", "/users/share-payslip", { userIds: staffIds });

          // const currentMonth = new Date();
          // await getPayrolls(currentMonth);
          showToast("Payslip shared successfully.", { type: "success" });
        } catch (err: any) {
          console.log("Error sharing payslip:", err);
          showToast(
            "Failed to share payslip. Please check your network and try again.",
            { type: "error" }
          );
        } finally {
          setLoading(false);
          getPayrolls(month);
        }
      }, true, "Yes, Share", "Cancel"
    );
  };


  const deletePayroll = async (payrollId: string) => {
    return openModal(
      `Delete Payroll Record`,
      `You're about to delete this payroll record. Please confirm before proceeding.`,
      async () => {
        setLoading(true);
        try {

          await sendRequest("DELETE", `/payrolls/${payrollId}/delete`);
          setPayrolls((prev) => prev.filter((pl) => pl.id !== payrollId));

          showToast(
            `Payroll Record Deleted Successfully.`,
            { type: "success" }
          );
        } catch (error: any) {
        } finally {
          setLoading(false);
        }
      }, true, "Yes, Delete", "Cancel"
    );
  };


  const filteredStaffs = () => {
    if (activeTab === "current" || activeTab === "former") {
      return staffs.filter(
        st => st.firstName.toLocaleLowerCase().includes(search.toLocaleLowerCase()) ||
          st?.lastName?.toLocaleLowerCase().includes(search.toLocaleLowerCase()) ||
          st?.portfolio?.toLocaleLowerCase().includes(search.toLocaleLowerCase())
      ) as any;
    } else if (activeTab === "payroll") {

      return payrolls.filter(
        pl => pl?.name?.toLocaleLowerCase()?.includes(search.toLocaleLowerCase()) ||
          pl?.position?.toLocaleLowerCase()?.includes(search.toLocaleLowerCase())
      );
    } else if (activeTab === "requests") {
      if (showRequestsList === "loan") {
        return loans.filter(
          pl => pl?.staffName?.toLocaleLowerCase().includes(search.toLocaleLowerCase())
        );
      } else if (showRequestsList === "leave") {
        return leaves.filter(
          pl => pl?.staffName?.toLocaleLowerCase().includes(search.toLocaleLowerCase())
        );

      }
    }
  }

  // console.log(staffs)
  return (
    <div ref={wrapperRef} className="p-6">
      <FullPageSpinner show={loading} />

      {/* Invite Modal */}
      <InviteModal
        show={showInviteModal}
        onClose={() => setShowInviteModal(false)}
      />

      {showPayrollModal && (
        <PayrollModal
          staff={selectedStaff as User}
          setStaffs={setStaffs}
          onClose={() => setShowPayrollModal(false)}
        />
      )}

      {showPayslip && selectedStaff && (
        <Payslip
          onClose={() => setShowPayslip(false)}
          employee={selectedStaff}
          createdAt={payrolls[0]?.createdAt}
          company={{
            name: COMPANY_NAME,
            logo: ""
          }}
          earnings={earnings}
          deductions={deductions}
        />
      )}

      {requestModal.show && (
        <RequestDetailsModal
          show={requestModal.show}
          onClose={() => setRequestModal(prev => ({ ...prev, show: false }))}
          type={requestModal.type}
          data={requestModal.data}
        />
      )}

      {/* Header */}
      <div className="flex justify-between items-center mb-2">
        <h1 className="text-2xl font-bold">Staff</h1>
        <div className="relative">
          <button
            onClick={() => setShowAddDropdown(!showAddDropdown)}
            className="inline-flex items-center rounded-lg bg-blue-600 px-4 py-2.5 text-xs font-semibold text-white shadow-sm transition hover:bg-blue-700"
          >
            <span className="mr-2 text-lg">{MdPersonAdd({})}</span>
            Add Staff <span className="ml-1">{MdArrowDropDown({})}</span>
          </button>

          {showAddDropdown && (
            <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border animate-fadeIn z-20">
              <button
                className="block w-full text-left px-4 py-2 hover:bg-gray-100 transition-all"
                onClick={() => {
                  setShowInviteModal(true);
                  setShowAddDropdown(false);
                }}
              >
                Share Invite Link
              </button>
              <button
                className="block w-full text-left px-4 py-2 hover:bg-gray-100 transition-all"
                onClick={addUserClick}
              >
                Add Staff Manually
              </button>
            </div>
          )}
        </div>
      </div>

      <DashboardHeader
        title={""}
        categories={[]}
        search={search}
        setSearch={setSearch}

        showFilter={showFilter}
      // onCategorySelect={setSelectedCategory}
      />

      {/* Tabs */}
      <div className="flex space-x-6 border-b mb-6">
        <button
          className={`pb-2 ${activeTab === "current" || activeTab === "former"
            ? "border-b-2 border-blue-600 text-blue-600"
            : "text-gray-500"
            }`}
          onClick={
            () => {
              setSearch("");
              getStaffs(false, true)
            }}
        >
          Staff Lists
        </button>
        {/* <button
          className={`pb-2 ${activeTab === "former"
            ? "border-b-2 border-blue-600 text-blue-600"
            : "text-gray-500"
            }`}
          onClick={() => {
            setSearch("");
            getStaffs(true, false)
          }}
        >
          Former Staff
        </button> */}
        <button
          className={`pb-2 ${activeTab === "payroll"
            ? "border-b-2 border-blue-600 text-blue-600"
            : "text-gray-500"
            }`}
          onClick={() => {
            const date = new Date();
            const currentYear = date.getFullYear();

            // const current = date.getMonth() + 1;
            const previous = date.getMonth();

            // const currentMonth = `${currentYear}-${String(current).padStart(2, "0")}`;
            const previousMonth = `${currentYear}-${String(previous).padStart(2, "0")}`
            setMonth(previousMonth);
            // setEnd(currentMonth);

            setMonth(`${currentYear}-${String(previous).padStart(2, "0")}`);
            setSearch("");
            setShowRequestsList(null);
            setActiveTab("payroll")
            getPayrolls(previousMonth);
          }
          }
        >
          Staff Payroll
        </button>
        <div className="relative">
          <button
            className={`pb-2 ${activeTab === "requests"
              ? "border-b-2 border-blue-600 text-blue-600"
              : "text-gray-500"
              }`}
            onClick={() => {
              setSearch("");
              setShowRequestDropdown(!showRequestDropdown)
            }}
          >
            Staff Requests
          </button>

          {showRequestDropdown && (
            <div className="absolute right-0 mt-2 bg-white rounded-lg shadow-lg border animate-fadeIn z-20 min-w-[150px] max-w-[90vw]">
              <button
                className="block w-full text-left px-4 py-2 hover:bg-gray-100 transition-all"
                onClick={() => {
                  setSearch("");
                  fetchRequests("loan");
                  setActiveTab("requests");
                  setShowRequestsList("loan");
                  setShowRequestDropdown(false);
                }}
              >
                Loan Requests
              </button>
              <button
                className="block w-full text-left px-4 py-2 hover:bg-gray-100 transition-all"
                onClick={() => {
                  setSearch("");
                  fetchRequests("leave");
                  setActiveTab("requests");
                  setShowRequestsList("leave");
                  setShowRequestDropdown(false);
                }}
              >
                Leave Requests
              </button>
            </div>
          )}
        </div>

      </div>

      {showRBACModal && selectedStaff && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <RBACModal
            staff={selectedStaff}
            closeModal={() => setShowRBACModal(false)}
            onSave={(staffId, permissions) => upserPermission(staffId, permissions)}
          />
        </div>
      )}

      {showRosterModal && selectedStaff && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <FullPageSpinner show={loading} />
          {/* Modal container with max height and scroll */}
          <div className="bg-white rounded-xl shadow-lg w-full max-w-3xl p-6 relative max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setShowRosterModal(false)}
              className="absolute top-3 right-3 text-gray-600 hover:text-black"
            >
              {FaTimes({})}
            </button>

            <h2 className="text-xl font-bold mb-4">
              Update Roster — {selectedStaff.firstName} <span className="capitalize">{selectedStaff.lastName} ({selectedStaff.portfolio})</span>
            </h2>

            {/* Scrollable form grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {(Object.keys(rosterSchedule) as Day[]).map((day) => (
                <div
                  key={day}
                  className="flex flex-col border p-3 rounded-lg bg-gray-50"
                >
                  <h3 className="capitalize font-semibold mb-2">{day}</h3>

                  {/* <label className="text-xs text-gray-600">Shift</label> */}
                  <select
                    value={rosterSchedule[day].shift}
                    onChange={(e) =>
                      setRosterSchedule((prev) => ({
                        ...prev,
                        [day]: { ...prev[day], shift: e.target.value },
                      }))
                    }
                    className="border rounded-md px-2 py-1 text-sm mb-2"
                  >
                    {/* <option value="">Select Shift Type</option> */}
                    <option value="day">Day</option>
                    <option value="night">Night</option>
                    <option value="leave">Leave</option>
                    {/* <option value="off">Off</option> */}
                  </select>

                  {/* <label className="text-xs text-gray-600">Branch</label> */}
                  <select
                    value={rosterSchedule[day].branch}   // ✅ CORRECT
                    onChange={(e) =>
                      setRosterSchedule((prev) => ({
                        ...prev,
                        [day]: { ...prev[day], branch: e.target.value },
                      }))
                    }
                    className="border rounded-md px-2 py-1 text-sm mb-2 capitalize"
                  >
                    <option value="">Select branch</option>
                    {sites.map((site) => (
                      <option className="capitalize" key={site.id} value={site.id}>
                        {site.name}
                      </option>
                    ))}
                  </select>

                </div>
              ))}
            </div>

            <div className="flex justify-end mt-6 gap-3">
              <button
                onClick={() => setShowRosterModal(false)}
                className="bg-gray-200 text-gray-800 px-5 py-2 rounded-lg hover:bg-gray-300 transition"
              >
                Cancel
              </button>

              <button
                onClick={async () => await upsertRoster()}
                className="bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 transition"
              >
                Save Changes
              </button>
            </div>

          </div>
        </div>
      )}

      {activeTab === "payroll" && (
        <>
          {/* ---------- Collapsible Section for Payroll Filters ---------- */}
          <Section title="Payroll Filters & Actions">
            {/* Tabs / Filters */}
            <div className="flex flex-col sm:flex-row gap-4 mb-4">

              {/* Actions Card */}
              <div className="flex gap-2 flex-wrap sm:flex-nowrap bg-white p-4 rounded-xl shadow-sm">
                <button
                  onClick={sharePayslip}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all 
          bg-blue-600 text-white shadow hover:bg-blue-700"
                >
                  {FaShareAlt({})} Share Payslip
                </button>

                <button
                  onClick={() =>
                    printPayroll({
                      hotelName: COMPANY_NAME,
                      dateStr: month,
                      payrolls: payrolls,
                    })
                  }
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all
                bg-gray-700 text-white shadow hover:bg-gray-800"
                >
                  {FaPrint({})} Print
                </button>
              </div>

              {/* Month Range Filter Card */}
              <div className="flex flex-wrap items-center gap-3 bg-white p-4 rounded-xl shadow-sm flex-1">

                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium"></label>
                  <input
                    type="month"
                    className="px-2 py-1 border rounded-md text-sm"
                    value={month}
                    onChange={(e) => setMonth(e.target.value)}
                  />
                </div>

                <button
                  onClick={() => getPayrolls(month)}
                  className="flex items-center gap-1 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium shadow hover:bg-green-700"
                >
                  Apply
                </button>
              </div>
            </div>

            {/* Total Amount Card */}
            <div className="bg-white p-4 rounded-xl shadow-sm mb-4 text-gray-700 font-semibold text-base">
              Total Payroll Amount: ₦{netpay.toLocaleString()}
            </div>
          </Section>


          <Section title="Staff Payroll Overview">
            {/* Payroll Table */}
            <Table
              columns={payRollColumns("staff")}
              data={filteredStaffs()}
              rowsPerPage={5}
              tableMenu={(row: Payroll, closeMenu) => (
                <TableMenu
                  row={row}
                  options={[
                    {
                      label: "View Payslip",
                      onClick: (r: Payroll) => {
                        const staff = staffs.find(u => u.id === r.user.id);
                        if (staff) {
                          setSelectedStaff(staff);
                          setDeductions(initPayroll(staff, "deduct", r.deductions));
                          setEarnings(initPayroll(staff, "earn", r.earnings));
                          setShowPayslip(true);
                        }
                      },
                    },
                    {
                      label: "Delete",
                      onClick: (r) => {
                        deletePayroll(r.id);
                        closeMenu();
                      },
                      color: "text-red-500",
                    },
                  ]}
                  closeMenu={closeMenu}
                />
              )}
            />
          </Section>
        </>
      )
      }

      {
        showRequestsList && (
          <div className="mb-2">
            <h2 className="text-lg font-medium text-gray-600 tracking-wide">
              {showRequestsList === "loan" ? "Loan Requests" : "Leave Requests"}
            </h2>
          </div>
        )
      }

      {
        showRequestsList === "loan" && (
          <Table
            columns={requestTableColumn("loan", true)}
            data={filteredStaffs()}
            rowsPerPage={PAGINATION_PAGE_NUMBER}
            tableMenu={(row: Loan, closeMenu) => (
              <TableMenu
                row={row}
                options={[
                  {
                    label: "View Details",
                    color: "text-gray-700",
                    onClick: (loan: Loan) => {
                      setRequestModal({
                        type: "loan", data: loan, show: true
                      });
                    },
                  },

                  isApprovedByCurrentUser(row.approvals ?? []) && {
                    label: "Approve Loan",
                    color: "text-green-600",
                    onClick: (loan: Loan) => {
                      const isAccess = checkAccess(
                        "loanActionAccess", access, openModal, closeModal
                      );
                      if (!isAccess) return;
                      handleStaffRequestAction(
                        "loan", loan.id, "approve",
                        {
                          role: loan.staffRole,
                          portfolio: loan.staffPortfolio
                        }, loan.approvals);
                    },
                  },
                  isApprovedByCurrentUser(row.approvals ?? []) && {
                    label: "Reject Loan",
                    color: "text-orange-500",
                    onClick: (loan: Loan) => {
                      const isAccess = checkAccess(
                        "loanActionAccess", access, openModal, closeModal
                      );
                      if (!isAccess) return;
                      handleStaffRequestAction("loan", loan.id, "reject",
                        {
                          role: loan.staffRole,
                          portfolio: loan.staffPortfolio
                        }, loan.approvals);
                    },
                  },
                  {
                    label: "Delete",
                    color: "text-red-600",
                    onClick: (loan: Loan) => {
                      handleStaffRequestAction(
                        "loan", loan.id, "delete", {
                        role: loan.staffRole,
                        portfolio: loan.staffPortfolio
                      }, loan.approvals);
                    },
                  },
                ].filter(Boolean) as MenuOption[]}

                closeMenu={closeMenu}
              />
            )}
          />
        )
      }

      {
        showRequestsList === "leave" && (
          <Table
            columns={requestTableColumn("leave", true)}
            data={filteredStaffs()}
            rowsPerPage={PAGINATION_PAGE_NUMBER}
            tableMenu={(row: Leave, closeMenu) => (
              <TableMenu
                row={row}
                options={[
                  {
                    label: "View Details",
                    color: "text-gray-700",
                    onClick: (leave: Leave) => {
                      setRequestModal({
                        type: "leave", data: leave, show: true
                      });
                    },
                  },

                  isApprovedByCurrentUser(row.approvals ?? []) && {
                    label: "Approve Leave",
                    color: "text-green-600",
                    onClick: (leave: Leave) => {
                      const isAccess = checkAccess(
                        "leaveActionAccess", access, openModal, closeModal
                      );
                      if (!isAccess) return;
                      handleStaffRequestAction("leave", leave.id, "approve", {
                        role: leave.staffRole,
                        portfolio: leave.staffPortfolio
                      }, leave.approvals);
                    },
                  },
                  isApprovedByCurrentUser(row.approvals ?? []) && {
                    label: "Reject Leave",
                    color: "text-orange-500",
                    onClick: (leave: Leave) => {
                      const isAccess = checkAccess(
                        "leaveActionAccess", access, openModal, closeModal
                      );
                      if (!isAccess) return;
                      handleStaffRequestAction("leave", leave.id, "reject", {
                        role: leave.staffRole,
                        portfolio: leave.staffPortfolio
                      }, leave.approvals);
                    },
                  },
                  {
                    label: "Delete",
                    color: "text-red-600",
                    onClick: (leave: Leave) => {
                      handleStaffRequestAction("leave", leave.id, "delete");
                    },
                  },
                ].filter(Boolean) as MenuOption[]}
                closeMenu={closeMenu}
              />
            )}
          />
        )
      }

      {/* Staff Grid */}
      {
        (activeTab === "current" || activeTab === "former") && (
          <>
            {/* Tabs / Filters */}
            <div className="flex items-center gap-3 mb-4">
              <button
                onClick={
                  () => {
                    setSearch("");
                    getStaffs(false, true)
                  }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all
      ${activeTab === "current"
                    ? "bg-blue-600 text-white shadow"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
              >
                Current Staff
              </button>

              <button
                onClick={() => {
                  setSearch("");
                  getStaffs(true, false)
                }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all
      ${activeTab === "former"
                    ? "bg-red-600 text-white shadow"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
              >
                Former Staff
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {(filteredStaffs() ?? []).map((staff: any) => (


                <div
                  key={staff.id}
                  className="bg-white shadow rounded-lg p-4 flex flex-col hover:shadow-lg transition-shadow duration-300"
                >
                  <div className="flex justify-between relative">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center text-lg font-semibold">
                        <ProfileAvatar
                          photoUrl={staff?.photoUrl}
                          firstName={staff?.firstName}
                          size="w-12 h-12 "
                        />
                      </div>
                      <div>
                        <h2 className="font-semibold capitalize">{staff.firstName + " " + staff.lastName}</h2>
                        <p className="text-sm text-gray-500 capitalize">{staff.staffId}</p>
                        <p className="text-xs text-gray-400 capitalize">{staff.portfolio}</p>
                      </div>
                    </div>

                    <button
                      onClick={() =>
                        setOpenMenuId(openMenuId === staff.id ? null : staff.id)
                      }
                      className="hover:bg-gray-100 rounded-full p-1 transition-transform transform hover:scale-110"
                    >
                      <span className="h-5 w-5 text-gray-500">{MdMoreVert({})}</span>
                    </button>
                    {openMenuId === staff.id && (
                      <div className="absolute right-0 top-8 w-56 bg-white border rounded-xl shadow-lg animate-fadeIn z-30 overflow-hidden">
                        {!staff.isDelete && <>
                          {/* Edit Staff */}
                          <button
                            className="flex items-center w-full text-left px-4 py-2 hover:bg-gray-100 transition-all gap-3"
                            onClick={() => navigate(`/staffs/update?staffId=${staff.id}`)}
                          >
                            <span className="text-blue-600 text-lg">{MdEdit({})}</span>
                            <span>Edit Staff</span>
                          </button>

                          {/* Assign Permissions */}
                          <button
                            className="flex items-center w-full text-left px-4 py-2 hover:bg-gray-100 transition-all gap-3"
                            onClick={() => {
                              setSelectedStaff(staff);
                              setShowRBACModal(true);
                              setOpenMenuId(null);
                            }}
                          >
                            <span className="text-indigo-600 text-lg">{MdLockPerson({})}</span>
                            <span>Assign Permissions</span>
                          </button>

                          {/* Manage Roster */}
                          <button
                            className="flex items-center w-full text-left px-4 py-2 hover:bg-gray-100 transition-all gap-3"
                            onClick={() => {
                              setSelectedStaff(staff);
                              const fetchRoster = async () => {
                                try {
                                  const data = await sendRequest("GET", `/users/${staff.id}/roster`);

                                  setRosterSchedule(data.schedule);

                                  const response = await sendRequest("GET", "/sites");
                                  setSites(response);
                                } catch (err) {
                                  showToast("Failed to load roster. Try again.", { type: "error" });
                                }
                              };
                              fetchRoster();
                              setShowRosterModal(true);
                              setOpenMenuId(null);
                            }}
                          >
                            <span className="text-teal-600 text-lg">{MdCalendarToday({})}</span>
                            <span>Manage Roster</span>
                          </button>

                          {/* Manage Payroll */}
                          <button
                            className="flex items-center w-full text-left px-4 py-2 hover:bg-gray-100 transition-all gap-3"
                            onClick={() => {
                              setSelectedStaff(staff);
                              setShowPayrollModal(true);
                              setOpenMenuId(null);
                            }}
                          >

                            <span className="text-amber-600 text-lg">{MdOutlinePayments({})}</span>
                            <span>Manage Payroll</span>
                          </button>

                          {/* Send Message */}
                          {/* <button
                          className="flex items-center w-full text-left px-4 py-2 hover:bg-gray-100 transition-all gap-3"
                          onClick={() =>
                            openModal(
                              "Coming Soon 🚀",
                              "We’re still working on this feature. Stay tuned for the upcoming release.",
                              closeModal,
                              false,
                              "Close"
                            )
                          }
                        >
                          <span className="text-gray-500 text-lg">{MdMessage({})}</span>
                          <span>Send Message</span>
                        </button> */}
                        </>}

                        {/* Activate/Deactivate */}
                        <button
                          className={`flex items-center w-full text-left px-4 py-2 hover:bg-gray-100 transition-all gap-3 ${staff.isDelete ? "text-green-600" : "text-red-600"
                            }`}
                          onClick={() => confirmDeactivateModal(staff)}
                        >
                          <span>{MdPowerSettingsNew({})}</span>
                          <span>{staff.isDelete ? "Activate Account" : "Deactivate Account"}</span>
                        </button>

                        {/* Delete Account (only if already deactivated) */}
                        {staff.isDelete && (
                          <button
                            className="flex items-center text-red-600 w-full text-left px-4 py-2 hover:bg-gray-100 transition-all gap-3"
                            onClick={() => deleteUser(staff)}
                          >
                            <span>{MdDelete({})}</span>
                            <span>Delete Account</span>
                          </button>
                        )}
                      </div>

                    )}
                  </div>

                  <div className="mt-3 flex flex-col space-y-1">
                    <a
                      href={`mailto:${staff.email}`}
                      title={staff.email}
                      className="flex items-center text-blue-600 text-sm hover:underline"
                    >
                      <span className="h-4 w-4 mr-2">{MdMail({})}</span>
                      {staff.email.length > 30 ? `${staff.email.slice(0, 30)}...` : staff.email}
                    </a>
                    <a
                      href={`tel:${staff.phone}`}
                      className="flex items-center text-gray-600 text-sm hover:underline"
                    >
                      <span className="h-4 w-4 mr-2">{MdPhone({})}</span>{staff.phone}
                    </a>
                  </div>
                  <div className="mt-3 flex items-center">
                    <span
                      className={`w-3 h-3 rounded-full ${staff.isActive ? "bg-green-500" : "bg-gray-400"
                        }`}
                    ></span>
                    <span className="ml-2 text-sm">
                      {staff.isActive ? "Active" : "Inactive"}
                    </span>
                  </div>
                </div>
              ))}
              {filteredStaffs().length === 0 && (
                <p className="text-gray-500 col-span-full text-center">No staff found.</p>
              )}
            </div>
          </>
        )
      }
    </div >
  );
}